import { PrevHeaderBarWithTitleTemplate } from '#templates';
import { BoxIcon, Button } from '#atoms';
import Draggable from '#assets/svg/drag-button.svg';

import { useState } from 'react';
import useUploadProfile from '#pages/ProfileUpdate/hooks';
import { useUserInfo } from '#providers/UserInfo';
import { Profile } from '#types/account';
import { useToast } from '#providers/ToastProvider';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '#api/axios';
import { queryKeyFactory } from '#api/queryKeyFactory';
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable
} from '@dnd-kit/sortable';
import { getWindowHeight } from 'src/refactor_shared/lib/getWindowHeight';

export function ProfileEdit() {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 10,
        tolerance: 5
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 10,
        tolerance: 5
      }
    })
  );

  const queryClient = useQueryClient();
  const { userInfo, setUserInfo } = useUserInfo();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile[]>(userInfo?.profile ?? []);

  const { mutate } = useMutation({
    mutationFn: async (profile: string[]) =>
      await axios.patch('/accounts/profile-sequence', {
        sequence: profile
      }),
    onSuccess: () => {
      showToast({ message: '프로필 사진 편집을 완료하였습니다.' });
      queryClient.invalidateQueries({
        queryKey: queryKeyFactory.GET_MY_ACCOUNT()
      });
      navigate('/profile');
      return;
    }
  });

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const handleDragEnd = (event: any) => {
    const { over } = event;

    if (activeId && over) {
      const activeIndex = profile.findIndex(item => item.id === activeId);
      const overIndex = profile.findIndex(item => item.id === over.id);

      setProfile(arrayMove(profile, activeIndex, overIndex));
      // 아이템 순서 변경
    }
    setActiveId(null);
  };

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const { handlePostImages, handleDeleteImage } = useUploadProfile({
    images: userInfo?.profile ?? [],
    maxCount: 100
  });

  const onEditProfile = () => {
    if (!userInfo)
      return showToast({
        type: 'fail',
        message: '일시적인 오류입니다. 다시 시도해 주세요.'
      });
    const getIdList = profile.reduce((acc: string[], cur) => {
      acc.push(cur.id);
      return acc;
    }, []);
    setUserInfo({ ...userInfo, profile });
    mutate(getIdList);
  };

  return (
    <PrevHeaderBarWithTitleTemplate
      childrenContainerStyle={{ height: '100%' }}
      title="프로필 사진 편집"
      defaultBackground
    >
      <div className={`h-full p-20`}>
        <div className="flex flex-col justify-between h-full gap-40 overflow-hidden">
          <div className="flex flex-wrap items-center justify-start gap-30">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
            >
              <SortableContext items={profile} strategy={rectSortingStrategy}>
                {profile && profile.length
                  ? profile.map(value => {
                      return (
                        <SortableProfileImages
                          key={value.id}
                          value={value}
                          id={value.id}
                          handleDeleteImage={handleDeleteImage}
                        />
                      );
                    })
                  : null}
              </SortableContext>
            </DndContext>
            <div className="w-[calc(33%-20px)] relative aspect-[1/1] rounded-8 object-cover flex justify-center items-end mt-auto max-h-[276.66px]">
              <input
                type="file"
                id="profile"
                onChange={handlePostImages}
                className="absolute w-full h-full opacity-0 touch-none hidden"
                multiple
                accept="image/*"
              />
              <label
                htmlFor="profile"
                className="bg-[#4D495A] rounded-8 h-full w-full justify-center items-center flex touch-none"
              >
                <BoxIcon type="plus-white" onlyIcon width={14} height={14} />
              </label>
            </div>
          </div>

          <Button
            type="button"
            className="mt-auto py-8"
            onClick={onEditProfile}
          >
            프로필 사진 편집 완료하기
          </Button>
        </div>
      </div>
    </PrevHeaderBarWithTitleTemplate>
  );
}

const SortableProfileImages = ({
  id,
  value,
  handleDeleteImage
}: {
  id: string;
  value: any;
  handleDeleteImage: (id: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: `translate3d(${transform?.x || 0}px, ${transform?.y || 0}px, 0)`,
    transition
  };

  return (
    <div
      key={value.id}
      className={`w-[calc(33%-20px)] aspect-[1/1.2] rounded-8 touch-none flex justify-end flex-col items-center mt-auto`}
      style={style}
    >
      <img
        src={Draggable}
        width={40}
        height={40}
        ref={setNodeRef}
        {...listeners}
        {...attributes}
      />
      <div className="relative">
        <img
          src={value.resizedLink}
          alt="프로필 사진"
          className="object-cover w-full h-full select-none rounded-8 touch-none"
        />
        <button
          type="button"
          className="absolute -top-4 -right-5 w-24 h-24 rounded-full flex justify-center items-center bg-[#7750E9] touch-none"
          onClick={() => handleDeleteImage(value.id)}
        >
          <BoxIcon type="x-white" onlyIcon width={12} height={12} />
        </button>
      </div>
    </div>
  );
};
